import { BorderStyle, utils, WorkSheet } from "@sheet/core";
import {
    AccountingReportComparisonDiff,
    AccountingReportRatioType,
} from "../constants";
import { NumberFormatValues } from "@common/schemas/domains/reportSettings/number-format-setting.schema";
import {
    NumberFormatAmountScale,
    NumberFormatCurrencyPlacement,
    NumberFormatCurrencySymbol,
    NumberFormatNegativeFormat,
    NumberFormatZeroDisplay,
} from "@common/types/reports/number-format-settings";

export interface CellRange {
    startRow: number;
    startColumn: number;
    endRow?: number;
    endColumn?: number;
}

export const SHEET_ACCOUNTING_FORMAT_NO_CURRENCY =
    '_(* #,##0.00_);_(* \\(#,##0.00\\);_(* "-"??_);_(@_)';
export const SHEET_ACCOUNTING_FORMAT_WITH_CURRENCY =
    '_("$"* #,##0.00_);_("$"* \\(#,##0.00\\);_("$"* "-"??_);_(@_)';

// Use explicit +/- signs instead of accounting parentheses
export const SHEET_DIFFERENCE_ABSOLUTE_FORMAT =
    '_("+$"* #,##0.00_);_("-$"* #,##0.00_);_(* "-"??_);_(@_)';
// Percentage format with explicit +/- signs
export const SHEET_DIFFERENCE_PERCENTAGE_FORMAT =
    '_(+0.00%_);_(-0.00%_);_(* "-"??_);_(@_)';
export const SHEET_PERCENTAGE_FORMAT = '_(0.0%_);_(-0.0%_);_(* "-"??_);_(@_)';

interface CellFormatPeriodParams {
    comparisonDiff?: AccountingReportComparisonDiff;
    ratioType?: AccountingReportRatioType;
}

const DIFFERENCE_CELL_FORMATS: Record<AccountingReportComparisonDiff, string> =
    {
        [AccountingReportComparisonDiff.ABSOLUTE]:
            SHEET_DIFFERENCE_ABSOLUTE_FORMAT,
        [AccountingReportComparisonDiff.PERCENTAGE]:
            SHEET_DIFFERENCE_PERCENTAGE_FORMAT,
    };

export function getCellFormatForPeriod({
    comparisonDiff,
    ratioType,
}: CellFormatPeriodParams): string {
    if (ratioType) {
        return SHEET_PERCENTAGE_FORMAT;
    }
    if (!comparisonDiff) {
        return SHEET_ACCOUNTING_FORMAT_NO_CURRENCY;
    }
    return DIFFERENCE_CELL_FORMATS[comparisonDiff];
}

export type ExcelCellKind =
    | "amount"
    | "absolute-difference"
    | "percentage-difference"
    | "ratio";

interface CellKindParams {
    comparisonDiff?: AccountingReportComparisonDiff;
    ratioType?: AccountingReportRatioType;
}

const DIFFERENCE_CELL_KINDS: Record<
    AccountingReportComparisonDiff,
    ExcelCellKind
> = {
    [AccountingReportComparisonDiff.ABSOLUTE]: "absolute-difference",
    [AccountingReportComparisonDiff.PERCENTAGE]: "percentage-difference",
};

export function getExcelCellKind({
    comparisonDiff,
    ratioType,
}: CellKindParams): ExcelCellKind {
    if (ratioType) {
        return "ratio";
    }
    if (!comparisonDiff) {
        return "amount";
    }
    return DIFFERENCE_CELL_KINDS[comparisonDiff];
}

const CURRENCY_TEXT: Record<NumberFormatCurrencySymbol, string> = {
    [NumberFormatCurrencySymbol.NONE]: "",
    [NumberFormatCurrencySymbol.DOLLAR]: "$",
    [NumberFormatCurrencySymbol.US_DOLLAR]: "US$ ",
    [NumberFormatCurrencySymbol.USD]: "USD ",
};

function quotedLiteral(text: string): string {
    return text ? `"${text}"` : "";
}

const SCALE_TRAILING_COMMAS: Record<NumberFormatAmountScale, string> = {
    [NumberFormatAmountScale.FULL]: "",
    [NumberFormatAmountScale.THOUSANDS]: ",",
    [NumberFormatAmountScale.MILLIONS]: ",,",
};

interface BuildNumberCoreParams {
    decimals: number;
    hasGrouping: boolean;
    scaleCommas: string;
}

function buildNumberCore({
    decimals,
    hasGrouping,
    scaleCommas,
}: BuildNumberCoreParams): string {
    const integer = hasGrouping ? "#,##0" : "0";
    const fraction = decimals > 0 ? `.${"0".repeat(decimals)}` : "";
    return `${integer}${fraction}${scaleCommas}`;
}

function buildNegativeSection(
    magnitude: string,
    negativeFormat: NumberFormatNegativeFormat,
): string {
    switch (negativeFormat) {
        case NumberFormatNegativeFormat.PARENTHESES:
            return `\\(${magnitude}\\)`;
        case NumberFormatNegativeFormat.MINUS_LEFT:
            return `-${magnitude}`;
        case NumberFormatNegativeFormat.MINUS_RIGHT:
            return `${magnitude}-`;
    }
}

function buildZeroSection(
    zeroDisplay: NumberFormatZeroDisplay,
    magnitude: string,
): string {
    switch (zeroDisplay) {
        case NumberFormatZeroDisplay.DASH:
            return '"-"';
        case NumberFormatZeroDisplay.BLANK:
            return "";
        case NumberFormatZeroDisplay.ZERO:
            return magnitude;
    }
}

interface BuildAccountingPatternParams {
    core: string;
    currencyText: string;
    decimals: number;
    kind: ExcelCellKind;
    isNegativeInRed: boolean;
}

// Excel's Accounting layout: `_(`/`_)` reserve the bracket gutters so positives
// line up with parenthesised negatives, `* ` pushes the currency symbol left and
// the digits right, and the `?` run pads the zero dash under the decimals. It is
// only correct for parenthesised negatives and a dash zero, so
// `buildNumberFormatPattern` falls back to a plain pattern for any other choice
// rather than emitting padding that lies about the format it wraps.
function buildAccountingPattern({
    core,
    currencyText,
    decimals,
    kind,
    isNegativeInRed,
}: BuildAccountingPatternParams): string {
    const currency = quotedLiteral(currencyText);
    const signedCurrency =
        kind === "absolute-difference"
            ? quotedLiteral(`+${currencyText}`)
            : currency;
    const positive = `_(${signedCurrency}* ${core}_)`;
    const negativeBody = `_(${currency}* \\(${core}\\)`;
    const negative = isNegativeInRed ? `[Red]${negativeBody}` : negativeBody;
    const zero = `_(${currency}* "-"${"?".repeat(decimals)}_)`;
    return `${positive};${negative};${zero};_(@_)`;
}

interface BuildNumberFormatPatternParams {
    numberFormat: NumberFormatValues;
    kind: ExcelCellKind;
}

// Display-only: the stored cell value stays the raw unrounded, unscaled number
// (amount scale uses Excel's trailing-comma display factor).
export function buildNumberFormatPattern({
    numberFormat,
    kind,
}: BuildNumberFormatPatternParams): string {
    const isPercent = kind === "percentage-difference" || kind === "ratio";
    const isScaled = kind === "amount" || kind === "absolute-difference";

    const decimals = isPercent
        ? numberFormat.percentDecimalPlaces
        : numberFormat.decimalPlaces;
    const scaleCommas = isScaled
        ? SCALE_TRAILING_COMMAS[numberFormat.amountScale]
        : "";
    const isCurrencyInHeaders =
        numberFormat.currencyPlacement ===
        NumberFormatCurrencyPlacement.COLUMN_HEADERS;
    const currencyText =
        isPercent || isCurrencyInHeaders
            ? ""
            : CURRENCY_TEXT[numberFormat.currencySymbol];
    const percentSuffix = isPercent ? "%" : "";

    const core = buildNumberCore({
        decimals,
        hasGrouping: numberFormat.hasThousandsSeparator,
        scaleCommas,
    });

    const hasAccountingLayout =
        !isPercent &&
        numberFormat.negativeFormat ===
            NumberFormatNegativeFormat.PARENTHESES &&
        numberFormat.zeroDisplay === NumberFormatZeroDisplay.DASH;
    if (hasAccountingLayout) {
        return buildAccountingPattern({
            core,
            currencyText,
            decimals,
            kind,
            isNegativeInRed: numberFormat.isNegativeInRed,
        });
    }

    const magnitude = `${quotedLiteral(currencyText)}${core}${percentSuffix}`;

    const positive =
        kind === "absolute-difference" ? `+${magnitude}` : magnitude;
    const negativeInner = buildNegativeSection(
        magnitude,
        numberFormat.negativeFormat,
    );
    const negative = numberFormat.isNegativeInRed
        ? `[Red]${negativeInner}`
        : negativeInner;
    const zero = buildZeroSection(numberFormat.zeroDisplay, magnitude);

    return `${positive};${negative};${zero}`;
}

export const ROUNDING_FOOTNOTE_TEXT = "Totals may not add due to rounding.";

export function buildScaleDisclosureText(
    numberFormat: NumberFormatValues,
): string {
    if (numberFormat.amountScale === NumberFormatAmountScale.THOUSANDS) {
        return "(in thousands)";
    }
    if (numberFormat.amountScale === NumberFormatAmountScale.MILLIONS) {
        return "(in millions)";
    }
    return "";
}

interface ResolveCellFormatParams extends CellKindParams {
    numberFormat?: NumberFormatValues;
}

// A missing `numberFormat` means the caller did not pass presentation
// (non-accounting sheets), so the cell keeps the hardcoded default format.
export function resolveCellFormat({
    numberFormat,
    comparisonDiff,
    ratioType,
}: ResolveCellFormatParams): string {
    if (!numberFormat) {
        return getCellFormatForPeriod({ comparisonDiff, ratioType });
    }
    return buildNumberFormatPattern({
        numberFormat,
        kind: getExcelCellKind({ comparisonDiff, ratioType }),
    });
}

const DIFFERENCE_EXCEL_DIVISORS: Record<
    AccountingReportComparisonDiff,
    number
> = {
    [AccountingReportComparisonDiff.ABSOLUTE]: 1,
    [AccountingReportComparisonDiff.PERCENTAGE]: 100,
};

interface FormatDiffValueParams {
    value: number;
    comparisonDiff?: AccountingReportComparisonDiff;
    ratioType?: AccountingReportRatioType;
}

export function formatDiffValueForExcel({
    value,
    comparisonDiff,
    ratioType,
}: FormatDiffValueParams): number {
    if (ratioType) {
        return value / 100;
    }
    return comparisonDiff
        ? value / DIFFERENCE_EXCEL_DIVISORS[comparisonDiff]
        : value;
}

export function disableGridLines(ws: WorkSheet) {
    ws["!gridlines"] = false;
}

// pass top-left cell of area that is not frozen
export function freezePane(ws: WorkSheet, topLeftCell: string) {
    ws["!freeze"] = topLeftCell;
}

export function setLink(ws: WorkSheet, cellCords: string, link: string) {
    const cell = getCell(ws, cellCords);

    cell.l = {
        Target: link,
    };
}

export function getColumn(ws: WorkSheet, index: number) {
    ws["!cols"] ??= [];

    ws["!cols"][index] ??= {};

    return ws["!cols"][index];
}

export function getRow(ws: WorkSheet, index: number) {
    /* create !cols array if it does not exist */
    ws["!rows"] ??= [];

    /* create column metadata object if it does not exist */
    ws["!rows"][index] ??= {};

    return ws["!rows"][index];
}

export function mergeCells(ws: WorkSheet, range: CellRange) {
    ws["!merges"] ??= [];

    ws["!merges"].push({
        s: { r: range.startRow, c: range.startColumn },
        e: {
            r: range.endRow ?? range.startRow,
            c: range.endColumn ?? range.startColumn,
        },
    });
}

export function getCell(ws: WorkSheet, cellCords: string) {
    if (!ws[cellCords]) {
        throw new Error(`Cell ${cellCords} does not exist in the worksheet`);
    }

    return ws[cellCords];
}

export function getCellStyles(ws: WorkSheet, cellCords: string) {
    const cell = getCell(ws, cellCords);

    cell.s ??= {};

    return cell.s;
}

export function setFontSize(ws: WorkSheet, cellCords: string, size: number) {
    const cellStyles = getCellStyles(ws, cellCords);

    cellStyles.sz = size;
}

export function boldCell(ws: WorkSheet, cellCords: string) {
    const cellStyles = getCellStyles(ws, cellCords);

    cellStyles.bold = true;
}

export function underlineCell(ws: WorkSheet, cellCords: string) {
    const cellStyles = getCellStyles(ws, cellCords);

    cellStyles.underline = true;
}

export function setTextColor(ws: WorkSheet, cellCords: string, color: string) {
    const cellStyles = getCellStyles(ws, cellCords);

    cellStyles.color = { rgb: color };
}

export function setBackgroundColor(
    ws: WorkSheet,
    cellCords: string,
    color: string,
) {
    const cellStyles = getCellStyles(ws, cellCords);

    cellStyles.patternType = "solid";
    cellStyles.fgColor = { rgb: color };
}

export function addCellFormatting(
    ws: WorkSheet,
    cellCords: string,
    formatting: string,
) {
    const cell = getCell(ws, cellCords);

    cell.z = formatting;
}

interface BorderSpec {
    direction: "top" | "bottom" | "left" | "right";
    style: BorderStyle;
    // rgb color, defaults to 0x000000
    color?: number;
}

export function addCellBorder(
    ws: WorkSheet,
    cellCords: string,
    { direction, style, color }: BorderSpec,
) {
    const cellStyles = getCellStyles(ws, cellCords);

    cellStyles[direction] = {
        style,
        color: { rgb: color },
    };
}

const NUMERIC_LITERAL_RE = /^-?\d+(\.\d+)?$/;
const CELL_REFERENCE_RE = /^[A-Z]{1,3}\d+$/;
const MAX_RESOLVE_PASSES = 10;

interface UnresolvedFallback {
    v: string | number;
    t: string;
    w: string;
}

const PLACEHOLDER_FALLBACK: UnresolvedFallback = {
    v: "\u2026",
    t: "s",
    w: "\u2026",
};
const ZERO_FALLBACK: UnresolvedFallback = { v: 0, t: "n", w: "0" };

/**
 * For XLSX files where formulas were not evaluated (e.g. opened with data_only),
 * attempt to fill in unevaluated formula cells by resolving numeric literals
 * and simple cell references iteratively.
 *
 * @param unresolvedFallback Controls what value unresolvable cells get:
 *   - "placeholder": display "…" (for UI preview)
 *   - "zero": use 0 (for numeric computation / parsing)
 * @returns true if there are still unresolved non-literal formulas
 */
export function fillUnevaluatedFormulaCells(
    worksheet: WorkSheet,
    unresolvedFallback: "placeholder" | "zero" = "placeholder",
): boolean {
    const ref = worksheet["!ref"];
    if (!ref) {
        return false;
    }

    const range = utils.decode_range(ref);

    let hasEvaluatedNonZero = false;
    const unevaluatedAddrs: string[] = [];

    for (let row = range.s.r; row <= range.e.r; row++) {
        for (let col = range.s.c; col <= range.e.c; col++) {
            const addr = utils.encode_cell({ r: row, c: col });
            const cell = worksheet[addr];
            if (!cell?.f) {
                continue;
            }

            if (cell.v != null && cell.v !== 0) {
                hasEvaluatedNonZero = true;
            } else {
                unevaluatedAddrs.push(addr);
            }
        }
    }

    if (hasEvaluatedNonZero || unevaluatedAddrs.length === 0) {
        return false;
    }

    const resolved = new Set<string>();

    for (const addr of unevaluatedAddrs) {
        const cell = worksheet[addr];
        if (NUMERIC_LITERAL_RE.test(cell.f)) {
            cell.v = parseFloat(cell.f);
            cell.t = "n";
            cell.w = cell.f;
            resolved.add(addr);
        }
    }

    let pending = unevaluatedAddrs.filter((addr) => !resolved.has(addr));

    for (
        let pass = 0;
        pass < MAX_RESOLVE_PASSES && pending.length > 0;
        pass++
    ) {
        const stillPending: string[] = [];

        for (const addr of pending) {
            const cell = worksheet[addr];
            if (!CELL_REFERENCE_RE.test(cell.f)) {
                stillPending.push(addr);
                continue;
            }

            const target = worksheet[cell.f];
            if (!target || !target.f) {
                cell.v = target?.v ?? "";
                cell.t = target?.t ?? "s";
                cell.w = target?.w ?? "";
                resolved.add(addr);
                continue;
            }

            if (!resolved.has(cell.f)) {
                stillPending.push(addr);
                continue;
            }

            cell.v = target.v;
            cell.t = target.t;
            cell.w = target.w;
            resolved.add(addr);
        }

        if (stillPending.length === pending.length) {
            break;
        }
        pending = stillPending;
    }

    const hasNonLiteralFormulas = pending.length > 0;
    const fallback =
        unresolvedFallback === "zero" ? ZERO_FALLBACK : PLACEHOLDER_FALLBACK;
    for (const addr of pending) {
        const cell = worksheet[addr];
        cell.v = fallback.v;
        cell.t = fallback.t;
        cell.w = fallback.w;
    }

    return hasNonLiteralFormulas;
}
